import { useEffect, useState } from 'react';

import { Button } from '@willow/shared-web';
import { PortalRouterMatch } from '@willow/types-iso';

import { GO_NOW, SINGLE_BODY, SINGLE_DOCUMENT_TITLE, SINGLE_TITLE, USE_DIFFERENT_EMAIL } from './findPortalCopy';
import { FindPortalLayout } from './FindPortalLayout';
import { FindPortalLinkButton } from './FindPortalLinkButton';
import { LenderRow } from './LenderRow';

import s from './FindPortal.module.scss';

interface Props {
  match: PortalRouterMatch;
  handoffMs: number;
  onGoNow: () => void;
  onUseDifferentEmail: () => void;
}

export const SingleMatchScreen = ({ match, handoffMs, onGoNow, onUseDifferentEmail }: Props) => {
  const [filled, setFilled] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  // Both of these have to land after the first paint. The fill needs its zero-width frame
  // for the transition to run from, and the live region has to be in the document empty
  // before it gains text, or the announcement is dropped on the screen the page then
  // navigates away from on its own.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setFilled(true);
      setAnnouncement(`${SINGLE_TITLE(match.name)}. ${SINGLE_BODY}`);
    });
    return () => cancelAnimationFrame(frame);
  }, [match.name]);

  return (
    <FindPortalLayout
      testId="find-portal-screen-single"
      documentTitle={SINGLE_DOCUMENT_TITLE}
      title={SINGLE_TITLE(match.name)}
      description={SINGLE_BODY}
    >
      <div className={s.visuallyHidden} role="status" aria-live="polite">
        {announcement}
      </div>
      <div className={s.lenderBox}>
        <LenderRow match={match} />
      </div>
      <div className={s.progressTrack} aria-hidden="true">
        <div
          className={s.progressFill}
          data-testid="find-portal-progress"
          style={{ transitionDuration: `${handoffMs}ms`, width: filled ? '100%' : '0%' }}
        />
      </div>
      <div>
        <Button
          size="lg"
          className="w-100"
          innerClassName="justify-content-center"
          data-testid="find-portal-go-now"
          onClick={onGoNow}
        >
          {GO_NOW}
        </Button>
        <FindPortalLinkButton data-testid="find-portal-use-different-email" onClick={onUseDifferentEmail}>
          {USE_DIFFERENT_EMAIL}
        </FindPortalLinkButton>
      </div>
    </FindPortalLayout>
  );
};
